import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Layers,
  Lightbulb,
  Mic,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import {
  DILTS_LEVELS,
  DILTS_STORY_STEPS,
  ELEVATE_PROMPTS,
  autoFillPitches,
  canAdvance,
  createDefaultDiltsStoryState,
  levelForStep,
  nextStep,
  prevStep,
  stepIndex,
  type DiltsLevelId,
  type DiltsStoryState,
  type DiltsStoryStep,
} from '../lib/diltsLogicalLevels';

const STORAGE_KEY = 'lab_dilts_logical_levels_story_v01';

function loadState(): DiltsStoryState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createDefaultDiltsStoryState();
    const parsed = JSON.parse(raw) as Partial<DiltsStoryState>;
    return {
      ...createDefaultDiltsStoryState(),
      ...parsed,
      levels: { ...createDefaultDiltsStoryState().levels, ...parsed.levels },
      elevate: { ...createDefaultDiltsStoryState().elevate, ...parsed.elevate },
      pitches: { ...createDefaultDiltsStoryState().pitches, ...parsed.pitches },
    };
  } catch {
    return createDefaultDiltsStoryState();
  }
}

function saveState(state: DiltsStoryState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface LogicalLevelsStoryProps {
  embedded?: boolean;
  onComplete?: (state: DiltsStoryState) => void;
}

export const LogicalLevelsStory: React.FC<LogicalLevelsStoryProps> = ({
  embedded = false,
  onComplete,
}) => {
  const [step, setStep] = useState<DiltsStoryStep>('intro');
  const [state, setState] = useState<DiltsStoryState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const progressPct = useMemo(
    () => ((stepIndex(step) + 1) / DILTS_STORY_STEPS.length) * 100,
    [step],
  );

  const updateLevel = useCallback((id: DiltsLevelId, value: string) => {
    setState(prev => ({
      ...prev,
      levels: { ...prev.levels, [id]: value },
    }));
  }, []);

  const updateElevate = useCallback((field: keyof DiltsStoryState['elevate'], value: string) => {
    setState(prev => ({
      ...prev,
      elevate: { ...prev.elevate, [field]: value },
    }));
  }, []);

  const updatePitch = useCallback((field: keyof DiltsStoryState['pitches'], value: string) => {
    setState(prev => ({
      ...prev,
      pitches: { ...prev.pitches, [field]: value },
    }));
  }, []);

  const goNext = useCallback(() => {
    if (!canAdvance(step, state)) return;

    if (step === 'elevate') {
      setState(prev => ({
        ...prev,
        pitches: autoFillPitches(prev),
      }));
    }

    if (step === 'synthesize') {
      const completed = { ...state, completedAt: new Date().toISOString() };
      setState(completed);
      onComplete?.(completed);
    }

    const n = nextStep(step);
    if (n) setStep(n);
  }, [step, state, onComplete]);

  const goBack = useCallback(() => {
    const p = prevStep(step);
    if (p) setStep(p);
  }, [step]);

  const reset = useCallback(() => {
    setState(createDefaultDiltsStoryState());
    setStep('intro');
  }, []);

  const currentLevel = levelForStep(step);
  const inputClass = embedded
    ? 'w-full bg-surface-elevated border border-surface-border  px-3 py-2.5 font-sans text-sm text-text-secondary placeholder-text-dim focus:outline-none focus:border-[var(--green)]/40 resize-none'
    : 'w-full bg-[var(--bg-raised)] border border-charcoal-200  px-3 py-2.5 text-sm text-charcoal-800 placeholder-charcoal-400 focus:outline-none focus:border-consulting-slate-blue-400 resize-none';

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8'}>
      {!embedded && (
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Layers className="h-8 w-8 text-consulting-slate-blue-600" />
            <h1 className="text-3xl font-heading font-bold text-charcoal-900">
              DILTS Logical Levels
            </h1>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-200">
              v0.1
            </span>
          </div>
          <p className="text-charcoal-600 text-sm max-w-2xl mx-auto">
            Discover, articulate, and speak your story at the level the conversation requires —
            from environment to purpose. Based on Robert Dilts&apos; Logical Levels of Change.
          </p>
        </div>
      )}

      {embedded && (
        <div className="mb-2">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-widest mb-2">
            Story &amp; positioning · v0.1
          </div>
          <h2 className="font-serif text-2xl text-text-primary font-light">
            Tell Your Story at Every Level
          </h2>
          <p className="font-sans text-text-secondary text-sm mt-2 max-w-2xl">
            Most people stall at skills and tasks. Build a stack you can draw from — behavior when
            they want concrete, values and identity when they want compelling.
          </p>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-3xl mx-auto w-full'}>
        <div className="flex items-center justify-between mb-2">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest ${
              embedded ? 'text-text-dim' : 'text-charcoal-500'
            }`}
          >
            Step {stepIndex(step) + 1} of {DILTS_STORY_STEPS.length} —{' '}
            {DILTS_STORY_STEPS[stepIndex(step)]?.label}
          </span>
          <span className={`font-mono text-[10px] ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            {Math.round(progressPct)}%
          </span>
        </div>
        <div
          className={`h-1.5 rounded-full overflow-hidden ${
            embedded ? 'bg-surface-elevated' : 'bg-charcoal-100'
          }`}
        >
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              embedded ? 'bg-[var(--green)]' : 'bg-consulting-slate-blue-600'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div
        className={` border p-6 md:p-8 ${
          embedded
            ? 'bg-surface-card border-surface-border'
            : 'max-w-3xl mx-auto bg-[var(--bg-raised)] border-charcoal-200 shadow-sm'
        }`}
      >
        {step === 'intro' && (
          <IntroStep embedded={embedded} />
        )}

        {currentLevel && (
          <LevelStep
            embedded={embedded}
            level={currentLevel}
            value={state.levels[currentLevel.id]}
            onChange={v => updateLevel(currentLevel.id, v)}
            inputClass={inputClass}
          />
        )}

        {step === 'elevate' && (
          <ElevateStep
            embedded={embedded}
            state={state}
            onChange={updateElevate}
            inputClass={inputClass}
          />
        )}

        {step === 'synthesize' && (
          <SynthesizeStep
            embedded={embedded}
            state={state}
            onChange={updatePitch}
            inputClass={inputClass}
          />
        )}

        {step === 'results' && (
          <ResultsStep embedded={embedded} state={state} />
        )}
      </div>

      <div
        className={`flex items-center justify-between gap-4 ${
          embedded ? '' : 'max-w-3xl mx-auto'
        }`}
      >
        <button
          type="button"
          onClick={goBack}
          disabled={step === 'intro'}
          className={`inline-flex items-center gap-2 px-4 py-2  border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            embedded
              ? 'border-surface-border text-text-secondary hover:bg-surface-elevated'
              : 'border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
          }`}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={reset}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
              embedded ? 'text-text-dim hover:text-text-secondary' : 'text-charcoal-500 hover:text-charcoal-700'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>

          {step !== 'results' ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance(step, state)}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                embedded ? 'bg-[var(--green)] hover:bg-[var(--green)]' : 'bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700'
              }`}
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep('intro')}
              className={`inline-flex items-center gap-2 px-5 py-2  text-sm font-medium text-white ${
                embedded ? 'bg-[var(--green)] hover:bg-[var(--green)]' : 'bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700'
              }`}
            >
              Review again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function IntroStep({ embedded }: { embedded: boolean }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Lightbulb className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Why most introductions stall at Level 3
        </h2>
      </div>

      <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        When someone asks &ldquo;Tell me about yourself,&rdquo; most people list tasks and skills —
        behavior and capabilities. That makes them forgettable. What resonates is values, identity,
        and purpose — but you need all six levels in your pocket to match the conversation.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div
          className={` border p-4 ${
            embedded ? 'border-[var(--coral)]/20 bg-[var(--coral)]/5' : 'border-[var(--coral)] bg-[var(--coral-dim)]/50'
          }`}
        >
          <div className={`text-xs font-mono uppercase tracking-widest mb-2 ${embedded ? 'text-[var(--coral)]' : 'text-[var(--coral)]'}`}>
            Stuck at Behavior + Skills
          </div>
          <p className={`text-sm italic ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
            &ldquo;I manage cloud cost optimization at an enterprise SaaS company, working with FinOps
            teams.&rdquo;
          </p>
        </div>
        <div
          className={` border p-4 ${
            embedded ? 'border-[var(--green)]/20 bg-[var(--green)]/5' : 'border-[var(--green)] bg-[var(--green-dim)]/50'
          }`}
        >
          <div className={`text-xs font-mono uppercase tracking-widest mb-2 ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}`}>
            Values + Identity + Purpose
          </div>
          <p className={`text-sm italic ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
            &ldquo;I believe companies deserve visibility into cloud usage so they make strategic
            decisions, not just cost cuts. I help enterprise teams turn FinOps complexity into
            actionable insight.&rdquo;
          </p>
        </div>
      </div>

      <p className={`text-sm ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
        You&apos;ll write your story at each of Robert Dilts&apos; six logical levels, practice moving
        between them, then shape pitches for different conversational depths.
      </p>
    </div>
  );
}

function LevelStep({
  embedded,
  level,
  value,
  onChange,
  inputClass,
}: {
  embedded: boolean;
  level: (typeof DILTS_LEVELS)[number];
  value: string;
  onChange: (v: string) => void;
  inputClass: string;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
            embedded
              ? 'bg-[var(--green)]/20 text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]/30'
              : 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700 border border-consulting-slate-blue-200'
          }`}
        >
          {level.id}
        </div>
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-widest mb-1 ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
            Level {level.id} · {level.questionWord}
          </div>
          <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
            {level.title}
          </h2>
          <p className={`text-sm mt-1 ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
            {level.prompt}
          </p>
        </div>
      </div>

      <div
        className={` border p-4 text-sm ${
          embedded ? 'border-[var(--mustard)]/20 bg-[var(--mustard)]/5 text-text-secondary' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]/60 text-charcoal-700'
        }`}
      >
        <span className="font-medium">Watch for:</span> {level.commonMistake}
      </div>

      <label className="block space-y-2">
        <span className={`text-xs font-medium ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
          Your answer at this level
        </span>
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={level.example}
          rows={4}
          className={inputClass}
        />
      </label>

      <details className={`text-sm ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>
        <summary className="cursor-pointer hover:opacity-80">Example answer</summary>
        <p className={`mt-2 italic ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
          {level.example}
        </p>
      </details>
    </div>
  );
}

function ElevateStep({
  embedded,
  state,
  onChange,
  inputClass,
}: {
  embedded: boolean;
  state: DiltsStoryState;
  onChange: (field: keyof DiltsStoryState['elevate'], value: string) => void;
  inputClass: string;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ArrowUp className={`h-5 w-5 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Practice moving between levels
        </h2>
      </div>

      <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        Many people get stuck at lower levels. These prompts help you elevate — or descend —
        intentionally when the conversation shifts.
      </p>

      <div className="space-y-5">
        {ELEVATE_PROMPTS.map(prompt => (
          <div key={prompt.id} className="space-y-2">
            <div className="flex items-center gap-2">
              {prompt.move.startsWith('Up') ? (
                <ArrowUp className={`h-4 w-4 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
              ) : (
                <ArrowDown className={`h-4 w-4 ${embedded ? 'text-[var(--mustard)]' : 'text-[var(--mustard)]'}`} />
              )}
              <span
                className={`font-mono text-[10px] uppercase tracking-widest ${
                  embedded ? 'text-text-dim' : 'text-charcoal-400'
                }`}
              >
                {prompt.move}
              </span>
            </div>
            <p className={`text-sm font-medium ${embedded ? 'text-text-primary' : 'text-charcoal-800'}`}>
              {prompt.question}
            </p>
            <textarea
              value={state.elevate[prompt.field]}
              onChange={e => onChange(prompt.field, e.target.value)}
              rows={3}
              className={inputClass}
              placeholder="Write a short answer…"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function SynthesizeStep({
  embedded,
  state,
  onChange,
  inputClass,
}: {
  embedded: boolean;
  state: DiltsStoryState;
  onChange: (field: keyof DiltsStoryState['pitches'], value: string) => void;
  inputClass: string;
}) {
  const pitches = [
    {
      key: 'behaviorPitch' as const,
      icon: Mic,
      label: 'When they want concrete (Behavior + Skills)',
      hint: 'Tasks, context, capabilities — safe for networking small talk.',
    },
    {
      key: 'valuesPitch' as const,
      icon: Lightbulb,
      label: 'When they want compelling (Values + Identity)',
      hint: 'Interview openings, executive conversations, personal branding.',
    },
    {
      key: 'fullPitch' as const,
      icon: Sparkles,
      label: 'When they want the full arc (add Purpose)',
      hint: 'Keynotes, mission-driven roles, "why you" conversations.',
    },
  ];

  return (
    <div className="space-y-6">
      <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
        Shape your pitches
      </h2>
      <p className={`text-sm ${embedded ? 'text-text-secondary' : 'text-charcoal-600'}`}>
        We drafted these from your stack. Edit until each sounds like you — then use the right
        depth for the room you&apos;re in.
      </p>

      <div className="space-y-5">
        {pitches.map(({ key, icon: Icon, label, hint }) => (
          <label key={key} className="block space-y-2">
            <div className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
              <span className={`text-sm font-medium ${embedded ? 'text-text-primary' : 'text-charcoal-800'}`}>
                {label}
              </span>
            </div>
            <p className={`text-xs ${embedded ? 'text-text-dim' : 'text-charcoal-500'}`}>{hint}</p>
            <textarea
              value={state.pitches[key]}
              onChange={e => onChange(key, e.target.value)}
              rows={4}
              className={inputClass}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function ResultsStep({ embedded, state }: { embedded: boolean; state: DiltsStoryState }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Sparkles className={`h-6 w-6 ${embedded ? 'text-[var(--green)]' : 'text-consulting-slate-blue-600'}`} />
        <h2 className={`text-xl font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Your logical levels stack
        </h2>
      </div>

      <div className="space-y-3">
        {DILTS_LEVELS.map(level => (
          <div
            key={level.id}
            className={` border p-4 ${
              embedded ? 'border-surface-border bg-surface-elevated/40' : 'border-charcoal-100 bg-[var(--surface)]/80'
            }`}
          >
            <div className="flex items-baseline gap-2 mb-1">
              <span className={`font-mono text-xs ${embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'}`}>
                L{level.id}
              </span>
              <span className={`text-sm font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
                {level.title}
              </span>
              <span className={`text-[10px] font-mono ${embedded ? 'text-text-dim' : 'text-charcoal-400'}`}>
                {level.questionWord}
              </span>
            </div>
            <p className={`text-sm whitespace-pre-wrap ${embedded ? 'text-text-secondary' : 'text-charcoal-700'}`}>
              {state.levels[level.id] || '—'}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-4 pt-2">
        <h3 className={`text-sm font-semibold ${embedded ? 'text-text-primary' : 'text-charcoal-900'}`}>
          Ready-to-use pitches
        </h3>
        {(
          [
            ['Behavior depth', state.pitches.behaviorPitch],
            ['Values + Identity', state.pitches.valuesPitch],
            ['Full narrative', state.pitches.fullPitch],
          ] as const
        ).map(([title, text]) => (
          <div
            key={title}
            className={` border p-4 ${
              embedded ? 'border-[var(--green)]/20 bg-[var(--green)]/5' : 'border-consulting-slate-blue-200 bg-consulting-slate-blue-50/40'
            }`}
          >
            <div
              className={`font-mono text-[10px] uppercase tracking-widest mb-2 ${
                embedded ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-consulting-slate-blue-600'
              }`}
            >
              {title}
            </div>
            <p className={`text-sm leading-relaxed ${embedded ? 'text-text-secondary' : 'text-charcoal-800'}`}>
              {text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
